import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Link } from 'react-router-dom';
import { KC_COLORS } from '../theme';
import { ArticleFigure } from './ArticleFigure';
import { FaqAccordion } from './FaqAccordion';
import { KcIcon } from './KcIcon';
import { RelatedLinksSection } from './RelatedLinksSection';

const EXAMPLE_COLORS = {
  background: '#F3FAF5',
  border: '#BFDCC9',
  title: '#1B5E3B',
  body: '#1F2937',
};

const MISCONCEPTION_COLORS = {
  accent: '#E67E22',
  rowBg: '#F5F7F9',
  text: '#1A2B4C',
};

const INFO_CARD_TONES = {
  blue: {
    background: '#EFF6FF',
    border: '#BFDBFE',
    title: '#1E3A8A',
    icon: '#1E40AF',
  },
  slate: {
    background: '#F4F6F8',
    border: '#D5DEE8',
    title: '#1E293B',
    icon: '#334155',
  },
  indigo: {
    background: '#EEF2FF',
    border: '#C7D2FE',
    title: '#1E1B4B',
    icon: '#312E81',
  },
  navy: {
    background: '#F0F4F8',
    border: '#C5D4E3',
    title: '#0A1D3A',
    icon: '#112B4D',
  },
  teal: {
    background: '#E6F7F6',
    border: '#A7E0DC',
    title: '#0F766E',
    icon: '#009BAA',
  },
};

function InfoCardIcon({ name, color }) {
  if (name === 'help') {
    return <HelpOutlineOutlinedIcon sx={{ fontSize: 28, color }} />;
  }
  if (name === 'trendingUp') {
    return <TrendingUpOutlinedIcon sx={{ fontSize: 28, color }} />;
  }
  return <KcIcon name={name || 'document'} sx={{ fontSize: 28, color }} />;
}

function InfoCard({
  title,
  hideTitle = false,
  tone = 'blue',
  icon = 'document',
  children,
}) {
  const colors = INFO_CARD_TONES[tone] || INFO_CARD_TONES.blue;

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 md:p-6 text-left"
      style={{
        borderColor: colors.border,
        backgroundColor: colors.background,
      }}
    >
      <div className="flex items-start gap-3 md:gap-4">
        <span className="shrink-0 mt-0.5">
          <InfoCardIcon name={icon} color={colors.icon} />
        </span>
        <div className="min-w-0 flex-1 overflow-hidden">
          {!hideTitle && title ? (
            <h2
              className="kc-type-h3 m-0 mb-2 font-bold break-words"
              style={{ color: colors.title }}
            >
              {title}
            </h2>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}
function SectionHeading({ title, className = '' }) {
  if (!title) return null;
  return (
    <h2
      className={`kc-type-h3 text-left ${className}`}
    >
      {title}
    </h2>
  );
}

function Paragraphs({ paragraphs, className = '' }) {
  if (!paragraphs?.length) return null;

  const labelRe = /^(EXECUTIVE INSIGHT|EXECUTIVE PERSPECTIVE|BEST PRACTICE|THE EXECUTIVE BOTTOM LINE)$/;
  const nodes = [];
  for (let index = 0; index < paragraphs.length; index += 1) {
    const text = paragraphs[index];
    const next = paragraphs[index + 1];
    if (labelRe.test(String(text || '').trim()) && next) {
      nodes.push(
        <div
          key={`label-callout-${index}`}
          className="rounded-xl border px-4 py-3.5"
          style={{
            borderColor: '#A7E0DC',
            backgroundColor: KC_COLORS.tealLight,
          }}
        >
          <p
            className="m-0 mb-1.5 font-bold uppercase tracking-[0.06em]"
            style={{ color: KC_COLORS.teal, fontSize: '12px', lineHeight: '16px' }}
          >
            {String(text).trim()}
          </p>
          <p className="kc-type-body m-0" style={{ color: KC_COLORS.navy700 }}>
            {next}
          </p>
        </div>
      );
      index += 1;
      continue;
    }

    const isLabel = /^[A-Z][A-Z0-9 /&:,.-]{2,48}$/.test(String(text || '').trim());
    nodes.push(
      <p
        key={`${index}-${String(text).slice(0, 48)}`}
        className={`kc-type-body text-left m-0 ${isLabel ? 'font-bold tracking-wide' : ''}`}
        style={{ color: isLabel ? KC_COLORS.teal : KC_COLORS.navy700 }}
      >
        {text}
      </p>
    );
  }

  return <div className={`space-y-3.5 text-left ${className}`}>{nodes}</div>;
}

function Bullets({ bullets, tone = 'default' }) {
  if (!bullets?.length) return null;
  const marker =
    tone === 'practice'
      ? 'bg-[#009BAA]'
      : tone === 'takeaway'
        ? 'bg-[#009BAA]'
        : 'bg-[#0A1D3A]';

  return (
    <ul className="mt-3 space-y-2.5 text-left">
      {bullets.map((item) => (
        <li
          key={item}
          className="flex gap-2.5 kc-type-body text-left"
        >
          <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${marker}`} />
          <span className="text-left">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function parseNumberedTitle(title) {
  const match = String(title || '').match(/^(\d+)\.\s*(.+)$/);
  if (!match) return { number: null, label: title || '' };
  return { number: match[1], label: match[2] };
}

function BodyGroupItems({ items, colors, headerIcon }) {
  return items.map((item, index) => {
    const { number, label } = parseNumberedTitle(item.title);
    const isLast = index === items.length - 1;

    return (
      <div
        key={item.id || `${item.title}-${index}`}
        id={item.id}
        className="scroll-mt-28 py-5 md:py-6"
        style={{
          borderBottom: isLast ? 'none' : `1px solid ${KC_COLORS.border}`,
        }}
      >
        <div className="flex items-start gap-3 md:gap-4 text-left">
          {number ? (
            <span
              className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg kc-type-caption font-bold"
              style={{
                backgroundColor: colors.background,
                color: colors.icon,
                border: `1px solid ${colors.border}`,
              }}
            >
              {number}
            </span>
          ) : (
            <span className="shrink-0 mt-0.5">
              <InfoCardIcon
                name={item.icon || headerIcon}
                color={colors.icon}
              />
            </span>
          )}
          <div className="min-w-0 flex-1">
            {label ? (
              <h3
                className="kc-type-body font-bold m-0 mb-2"
                style={{ color: KC_COLORS.primaryNavy }}
              >
                {label}
              </h3>
            ) : null}
            <Paragraphs paragraphs={item.paragraphs} />
            <Bullets bullets={item.bullets} />
          </div>
        </div>
      </div>
    );
  });
}

/** One cohesive panel for consecutive body items (bodyGroup). */
function BodyGroupPanel({ section, hideTitle = false }) {
  const items = Array.isArray(section.items) ? section.items : [];
  if (!items.length) return null;

  const toneKey = items[0]?.tone || section.tone || 'indigo';
  const colors = INFO_CARD_TONES[toneKey] || INFO_CARD_TONES.indigo;
  const headerIcon = items[0]?.icon || section.icon || 'document';

  // Mobile accordion already provides the outer shell — render steps only.
  if (hideTitle) {
    return (
      <div className="text-left -mt-1">
        <BodyGroupItems
          items={items}
          colors={colors}
          headerIcon={headerIcon}
        />
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border overflow-hidden text-left shadow-[0_8px_28px_rgba(10,29,58,0.05)]"
      style={{
        borderColor: colors.border,
        backgroundColor: KC_COLORS.white,
      }}
    >
      {section.title ? (
        <div
          className="flex items-center gap-3 px-5 md:px-6 py-4 border-b"
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
          }}
        >
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
            style={{
              backgroundColor: KC_COLORS.white,
              boxShadow: `0 0 0 1px ${colors.border}`,
            }}
          >
            <InfoCardIcon name={headerIcon} color={colors.icon} />
          </span>
          <h2
            className="kc-type-h3 m-0 font-bold min-w-0"
            style={{ color: colors.title }}
          >
            {section.title}
          </h2>
        </div>
      ) : null}

      <div className="px-5 md:px-6">
        <BodyGroupItems
          items={items}
          colors={colors}
          headerIcon={headerIcon}
        />
      </div>
    </div>
  );
}

function SectionContent({ section, hideTitle = false }) {
  const title = hideTitle ? '' : section.title;

  switch (section.variant) {
    case 'summary':
      return (
        <InfoCard
          title={section.title}
          hideTitle={hideTitle}
          tone={section.tone || 'blue'}
          icon={section.icon || 'document'}
        >
          <Paragraphs paragraphs={section.paragraphs} />
          <Bullets bullets={section.bullets} />
        </InfoCard>
      );

    case 'chapterSummary':
      return (
        <div
          className="rounded-2xl border overflow-hidden text-left"
          style={{
            borderColor: KC_COLORS.border,
            backgroundColor: KC_COLORS.white,
          }}
        >
          <div
            className="flex items-center gap-3 px-5 md:px-6 py-4 border-b"
            style={{
              backgroundColor: KC_COLORS.primaryNavy,
              borderColor: KC_COLORS.primaryNavy,
            }}
          >
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg"
              style={{ backgroundColor: 'rgba(0,155,170,0.2)', color: KC_COLORS.teal }}
            >
              <KcIcon name="menuBook" sx={{ fontSize: 22 }} />
            </span>
            <div className="min-w-0">
              {section.eyebrow ? (
                <p
                  className="kc-type-caption font-bold uppercase tracking-[0.08em] mb-0.5"
                  style={{ color: KC_COLORS.teal }}
                >
                  {section.eyebrow}
                </p>
              ) : null}
              {title ? (
                <h2 className="kc-type-h3 m-0" style={{ color: '#FFFFFF' }}>
                  {title}
                </h2>
              ) : null}
            </div>
          </div>

          <div className="p-5 md:p-6">
            <div
              className="pl-4 md:pl-5 border-l-[3px] mb-5"
              style={{ borderColor: KC_COLORS.teal }}
            >
              <Paragraphs paragraphs={section.paragraphs} />
            </div>

            {(section.highlights || []).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {section.highlights.map((item, index) => (
                  <div
                    key={item.id}
                    className="rounded-xl border p-4 text-left"
                    style={{
                      borderColor: KC_COLORS.border,
                      backgroundColor: KC_COLORS.lightGrey,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full kc-type-caption font-bold"
                        style={{
                          backgroundColor: KC_COLORS.tealLight,
                          color: KC_COLORS.teal,
                        }}
                      >
                        {index + 1}
                      </span>
                      <p
                        className="kc-type-caption font-bold uppercase tracking-[0.04em] m-0"
                        style={{ color: KC_COLORS.navy700 }}
                      >
                        {item.label}
                      </p>
                    </div>
                    <p className="kc-type-body m-0" style={{ color: KC_COLORS.gray600 }}>
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      );

    case 'callout':
      return (
        <InfoCard
          title={section.title}
          hideTitle={hideTitle}
          tone={section.tone || 'teal'}
          icon={section.icon || 'trendingUp'}
        >
          <Paragraphs paragraphs={section.paragraphs} />
          <Bullets bullets={section.bullets} />
        </InfoCard>
      );

    case 'example':
      return (
        <div
          className="rounded-xl border p-5 md:p-6 text-left"
          style={{
            borderColor: EXAMPLE_COLORS.border,
            backgroundColor: EXAMPLE_COLORS.background,
          }}
        >
          <div className="flex items-start gap-4">
            <AssignmentOutlinedIcon
              sx={{
                fontSize: 28,
                mt: '2px',
                color: EXAMPLE_COLORS.title,
                flexShrink: 0,
              }}
            />
            <div className="min-w-0 flex-1">
              {!hideTitle ? (
                <h2
                  className="kc-type-h3 m-0 mb-2 font-bold"
                  style={{ color: EXAMPLE_COLORS.title }}
                >
                  {section.title}
                </h2>
              ) : null}
              {(section.paragraphs || []).length > 0 ? (
                <div className="space-y-3">
                  {section.paragraphs.map((text) => (
                    <p
                      key={text.slice(0, 64)}
                      className="kc-type-body m-0 text-left"
                      style={{ color: EXAMPLE_COLORS.body }}
                    >
                      {text}
                    </p>
                  ))}
                </div>
              ) : null}
              {(section.items || []).length > 0 ? (
                <div className="mt-4 space-y-4">
                  {section.items.map((item) => (
                    <div key={item.title} className="text-left">
                      <h3
                        className="kc-type-body font-bold m-0 mb-1"
                        style={{ color: EXAMPLE_COLORS.title }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="kc-type-body m-0"
                        style={{ color: EXAMPLE_COLORS.body }}
                      >
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );

    case 'table': {
      const columnCount = (section.columns || []).length;
      const tableMinWidth =
        columnCount >= 4 ? 'min-w-[720px]' : columnCount === 3 ? 'min-w-[640px]' : 'min-w-[520px]';

      return (
        <div className="text-left">
          <SectionHeading title={title} className="mb-3" />
          <Paragraphs paragraphs={section.paragraphs} />
          {(section.columns || []).length > 0 && (section.rows || []).length > 0 ? (
            <div
              className="mt-4 overflow-x-auto rounded-xl border shadow-[0_1px_2px_rgba(10,29,58,0.04)]"
              style={{ borderColor: KC_COLORS.border }}
            >
              <table className={`w-full ${tableMinWidth} border-collapse text-left`}>
                <thead>
                  <tr style={{ backgroundColor: KC_COLORS.primaryNavy }}>
                    {section.columns.map((column) => (
                      <th
                        key={column}
                        className="px-4 py-3.5 kc-type-caption font-bold uppercase tracking-[0.04em] border"
                        style={{
                          color: '#FFFFFF',
                          borderColor: 'rgba(255,255,255,0.12)',
                        }}
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, rowIndex) => (
                    <tr key={`${row[0]}-${rowIndex}`}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={`${rowIndex}-${cellIndex}`}
                          className={`px-4 py-3.5 kc-type-body align-top border ${
                            cellIndex === 0 ? 'font-semibold' : ''
                          }`}
                          style={{
                            color:
                              cellIndex === 0
                                ? KC_COLORS.primaryNavy
                                : KC_COLORS.navy700,
                            backgroundColor:
                              rowIndex % 2 === 0 ? KC_COLORS.white : KC_COLORS.lightGrey,
                            borderColor: KC_COLORS.border,
                          }}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {section.keyInsight ? (
            <div
              className="mt-4 rounded-xl border-l-4 px-4 py-3.5"
              style={{
                borderColor: KC_COLORS.teal,
                backgroundColor: KC_COLORS.tealLight,
              }}
            >
              <p
                className="kc-type-caption font-bold uppercase tracking-[0.04em] mb-1"
                style={{ color: KC_COLORS.teal }}
              >
                Key Insight
              </p>
              <p className="kc-type-body m-0" style={{ color: KC_COLORS.navy700 }}>
                {section.keyInsight}
              </p>
            </div>
          ) : null}
        </div>
      );
    }

    case 'quote':
      return (
        <div className="rounded-2xl bg-[#0A1D3A] p-5 md:p-7">
          {title ? (
            <h2 className="kc-type-h3 mb-4" style={{ color: '#FFFFFF' }}>
              {title}
            </h2>
          ) : null}
          <blockquote className="kc-type-body italic" style={{ color: 'rgba(255,255,255,0.95)' }}>
            “{section.quote}”
          </blockquote>
          {section.attribution ? (
            <p className="mt-4 kc-type-caption font-semibold" style={{ color: KC_COLORS.teal }}>
              — {section.attribution}
            </p>
          ) : null}
          {section.paragraphs?.length ? (
            <div className="mt-4 space-y-3">
              {section.paragraphs.map((text) => (
                <p
                  key={text.slice(0, 64)}
                  className="text-[15px] md:text-[16px] leading-[1.7] text-white/85"
                >
                  {text}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      );

    case 'benefits':
      return (
        <div className="text-left">
          {!hideTitle ? (
            <div className="flex items-center gap-2.5 mb-4">
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
                style={{
                  backgroundColor: KC_COLORS.tealLight,
                  color: KC_COLORS.teal,
                }}
              >
                <TrendingUpOutlinedIcon sx={{ fontSize: 20 }} />
              </span>
              <h2 className="kc-type-h3 m-0" style={{ color: KC_COLORS.primaryNavy }}>
                {section.title}
              </h2>
            </div>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {section.items?.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border p-4 md:p-5 text-left transition-shadow hover:shadow-[0_8px_20px_rgba(10,29,58,0.06)]"
                style={{
                  borderColor: KC_COLORS.border,
                  backgroundColor: KC_COLORS.white,
                }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-full"
                    style={{
                      backgroundColor: KC_COLORS.tealLight,
                      color: KC_COLORS.teal,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        fontSize: 18,
                        stroke: 'currentColor',
                        strokeWidth: 1.2,
                      }}
                    />
                  </span>
                  <div className="min-w-0">
                    <h3
                      className="kc-type-body font-bold m-0"
                      style={{ color: KC_COLORS.primaryNavy }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="mt-1.5 kc-type-body m-0"
                      style={{ color: KC_COLORS.gray600 }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'misconceptions':
      return (
        <div
          className="rounded-2xl border bg-white p-5 md:p-6 text-left"
          style={{ borderColor: KC_COLORS.border }}
        >
          {!hideTitle ? (
            <div className="flex items-center gap-2.5 mb-4">
              <LightbulbOutlinedIcon
                sx={{ fontSize: 26, color: MISCONCEPTION_COLORS.accent }}
              />
              <h2
                className="kc-type-h3 m-0 font-bold"
                style={{ color: MISCONCEPTION_COLORS.text }}
              >
                {section.title}
              </h2>
            </div>
          ) : null}
          <Paragraphs paragraphs={section.paragraphs} />
          <div className={`${section.paragraphs?.length ? 'mt-4' : ''} space-y-2.5`}>
            {section.items?.map((item) => (
              <div
                key={item.myth}
                className="rounded-xl px-4 py-3 md:px-5 md:py-3.5"
                style={{ backgroundColor: MISCONCEPTION_COLORS.rowBg }}
              >
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 md:gap-4 items-start md:items-center text-left">
                  <p
                    className="kc-type-body m-0"
                    style={{ color: MISCONCEPTION_COLORS.text }}
                  >
                    {item.myth}
                  </p>
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: MISCONCEPTION_COLORS.accent,
                      mx: { xs: 0, md: 0 },
                      mt: { xs: 0, md: '2px' },
                    }}
                  />
                  <p
                    className="kc-type-body m-0 font-semibold"
                    style={{ color: MISCONCEPTION_COLORS.text }}
                  >
                    {item.reality}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'practices': {
      const practices = (section.bullets || []).map((bullet, index) => {
        const separator = ' — ';
        const separatorIndex = bullet.indexOf(separator);
        if (separatorIndex === -1) {
          return { id: index + 1, title: bullet, description: '' };
        }
        return {
          id: index + 1,
          title: bullet.slice(0, separatorIndex).trim(),
          description: bullet.slice(separatorIndex + separator.length).trim(),
        };
      });

      return (
        <div
          className="rounded-2xl border overflow-hidden text-left shadow-[0_8px_28px_rgba(10,29,58,0.05)]"
          style={{ borderColor: '#CFF5F1', backgroundColor: KC_COLORS.white }}
        >
          {!hideTitle ? (
            <div
              className="flex items-center gap-3 px-5 md:px-6 py-4"
              style={{ backgroundColor: KC_COLORS.tealLight }}
            >
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
                style={{
                  backgroundColor: KC_COLORS.white,
                  color: KC_COLORS.teal,
                  boxShadow: '0 0 0 1px rgba(0,155,170,0.2)',
                }}
              >
                <KcIcon name="gear" sx={{ fontSize: 22 }} />
              </span>
              <div className="min-w-0">
                <p
                  className="kc-type-caption font-bold uppercase tracking-[0.08em] m-0 mb-0.5"
                  style={{ color: KC_COLORS.teal }}
                >
                  Adoption playbook
                </p>
                <h2 className="kc-type-h3 m-0" style={{ color: KC_COLORS.primaryNavy }}>
                  {section.title}
                </h2>
              </div>
            </div>
          ) : null}

          <div className="px-5 md:px-6 py-5 md:py-6">
            <Paragraphs paragraphs={section.paragraphs} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {practices.map((practice) => (
                <div
                  key={practice.id}
                  className="rounded-xl border p-4 md:p-5 text-left transition-shadow hover:shadow-[0_8px_20px_rgba(10,29,58,0.06)]"
                  style={{
                    borderColor: KC_COLORS.border,
                    backgroundColor: KC_COLORS.lightGrey,
                  }}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg kc-type-caption font-bold"
                      style={{
                        backgroundColor: KC_COLORS.teal,
                        color: KC_COLORS.white,
                      }}
                    >
                      {practice.id}
                    </span>
                    <div className="min-w-0">
                      <h3
                        className="kc-type-body font-bold m-0"
                        style={{ color: KC_COLORS.primaryNavy }}
                      >
                        {practice.title}
                      </h3>
                      {practice.description ? (
                        <p
                          className="kc-type-body mt-1.5 m-0"
                          style={{ color: KC_COLORS.gray600 }}
                        >
                          {practice.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    case 'takeaways': {
      const bullets = section.bullets || [];
      const columnCount = 3;
      const perColumn = Math.ceil(bullets.length / columnCount) || 1;
      const columns = Array.from({ length: columnCount }, (_, colIndex) =>
        bullets.slice(colIndex * perColumn, colIndex * perColumn + perColumn)
      ).filter((column) => column.length > 0);

      return (
        <div
          className="rounded-2xl border p-5 md:p-6 lg:p-7 text-left"
          style={{
            borderColor: '#D6E4F5',
            backgroundColor: '#F0F7FF',
          }}
        >
          {!hideTitle ? (
            <div className="flex items-center gap-3 mb-4 md:mb-5">
              <span
                className="inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0"
                style={{
                  backgroundColor: KC_COLORS.white,
                  color: KC_COLORS.primaryNavy,
                  boxShadow: '0 0 0 1px #D6E4F5',
                }}
              >
                <KcIcon name="target" sx={{ fontSize: 24 }} />
              </span>
              <h2 className="kc-type-h3 m-0" style={{ color: KC_COLORS.primaryNavy }}>
                {section.title}
              </h2>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {columns.map((column, colIndex) => (
              <ul key={`takeaway-col-${colIndex}`} className="m-0 p-0 list-none space-y-3">
                {column.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-left">
                    <CheckIcon
                      sx={{
                        fontSize: 20,
                        mt: '1px',
                        color: KC_COLORS.primaryNavy,
                        flexShrink: 0,
                        stroke: 'currentColor',
                        strokeWidth: 1.4,
                      }}
                    />
                    <span
                      className="kc-type-body"
                      style={{ color: KC_COLORS.navy700 }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      );
    }

    case 'worksheet':
      return (
        <div
          className="rounded-2xl border overflow-hidden text-left shadow-[0_8px_28px_rgba(10,29,58,0.06)]"
          style={{ borderColor: KC_COLORS.border, backgroundColor: KC_COLORS.white }}
        >
          {!hideTitle ? (
            <div
              className="flex items-center gap-3 px-5 md:px-6 py-4"
              style={{
                background:
                  'linear-gradient(135deg, #0A1D3A 0%, #112B4D 60%, #0E3A4A 100%)',
              }}
            >
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
                style={{ backgroundColor: 'rgba(0,155,170,0.22)', color: KC_COLORS.teal }}
              >
                <KcIcon name="assignment" sx={{ fontSize: 22 }} />
              </span>
              <div className="min-w-0">
                <p
                  className="kc-type-caption font-bold uppercase tracking-[0.08em] m-0 mb-0.5"
                  style={{ color: KC_COLORS.teal }}
                >
                  Leadership tool
                </p>
                <h2 className="kc-type-h3 m-0" style={{ color: '#FFFFFF' }}>
                  {section.title}
                </h2>
              </div>
            </div>
          ) : null}

          <div className="p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
              <div
                className="rounded-xl border p-4 md:p-5"
                style={{ borderColor: '#D6E4F5', backgroundColor: '#F3F7FC' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
                    style={{ backgroundColor: KC_COLORS.white, color: KC_COLORS.teal }}
                  >
                    <KcIcon name="insights" sx={{ fontSize: 18 }} />
                  </span>
                  <p
                    className="kc-type-caption font-bold uppercase tracking-[0.05em] m-0"
                    style={{ color: KC_COLORS.navy700 }}
                  >
                    Self-assessment
                  </p>
                </div>
                {section.intro ? (
                  <p className="kc-type-body mb-3" style={{ color: KC_COLORS.gray600 }}>
                    {section.intro}
                  </p>
                ) : null}
                <ol className="m-0 p-0 list-none space-y-2.5">
                  {(section.bullets || []).map((item, index) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5"
                      style={{ borderColor: KC_COLORS.border }}
                    >
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-md kc-type-caption font-bold"
                        style={{
                          backgroundColor: KC_COLORS.tealLight,
                          color: KC_COLORS.teal,
                        }}
                      >
                        {index + 1}
                      </span>
                      <p
                        className="kc-type-body m-0 font-medium"
                        style={{ color: KC_COLORS.primaryNavy }}
                      >
                        {item}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>

              <div
                className="rounded-xl border p-4 md:p-5"
                style={{ borderColor: '#CFF5F1', backgroundColor: KC_COLORS.tealLight }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
                    style={{ backgroundColor: KC_COLORS.white, color: KC_COLORS.teal }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </span>
                  <p
                    className="kc-type-caption font-bold uppercase tracking-[0.05em] m-0"
                    style={{ color: KC_COLORS.navy700 }}
                  >
                    Readiness checklist
                  </p>
                </div>
                {section.checklistIntro ? (
                  <p className="kc-type-body mb-3" style={{ color: KC_COLORS.gray600 }}>
                    {section.checklistIntro}
                  </p>
                ) : null}
                <ul className="m-0 p-0 list-none space-y-2">
                  {(section.checklist || []).map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5"
                      style={{ borderColor: 'rgba(0,155,170,0.25)' }}
                    >
                      <span
                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border"
                        style={{
                          borderColor: KC_COLORS.teal,
                          backgroundColor: 'rgba(0,155,170,0.08)',
                        }}
                        aria-hidden
                      />
                      <p
                        className="kc-type-body m-0"
                        style={{ color: KC_COLORS.primaryNavy }}
                      >
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      );

    case 'list':
      if (section.tone) {
        return (
          <InfoCard
            title={section.title}
            hideTitle={hideTitle}
            tone={section.tone}
            icon={section.icon || 'help'}
          >
            {section.intro ? (
              <p className="mb-2 kc-type-body" style={{ color: KC_COLORS.gray600 }}>
                {section.intro}
              </p>
            ) : null}
            <Paragraphs paragraphs={section.paragraphs} />
            <Bullets bullets={section.bullets} />
          </InfoCard>
        );
      }
      return (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6">
          <SectionHeading title={title} className="mb-3" />
          {section.intro ? (
            <p className="mb-2 text-[15px] md:text-[16px] text-[#6B7280]">{section.intro}</p>
          ) : null}
          <Paragraphs paragraphs={section.paragraphs} />
          <Bullets bullets={section.bullets} />
        </div>
      );

    case 'references':
      return (
        <div
          className={`text-left ${hideTitle ? '' : 'border-t pt-6 md:pt-8'}`}
          style={hideTitle ? undefined : { borderColor: KC_COLORS.border }}
        >
          <SectionHeading title={title} className="mb-1 text-left" />
          <p
            className={`kc-type-caption text-left ${hideTitle ? 'mb-3' : 'mb-4'}`}
            style={{ color: KC_COLORS.gray600 }}
          >
            Sources cited in this chapter
          </p>
          <ol className="m-0 list-none p-0 space-y-2.5 text-left">
            {(section.references || []).map((ref, index) => (
              <li
                key={ref.id}
                className="flex items-start gap-2 text-left"
              >
                <span
                  className="kc-type-caption font-semibold shrink-0 tabular-nums leading-[24px]"
                  style={{ color: KC_COLORS.teal }}
                >
                  [{index + 1}]
                </span>
                <cite className="min-w-0 not-italic text-left">
                  <p className="kc-type-body text-left" style={{ color: KC_COLORS.primaryNavy }}>
                    <span className="font-semibold">{ref.authors}</span>
                    {ref.year ? (
                      <span style={{ color: KC_COLORS.gray600 }}> ({ref.year}). </span>
                    ) : (
                      '. '
                    )}
                    <span className="italic">{ref.title}</span>
                    {ref.source ? (
                      <>
                        . <span style={{ color: KC_COLORS.navy700 }}>{ref.source}</span>
                      </>
                    ) : null}
                    {ref.urlLabel ? (
                      <span style={{ color: KC_COLORS.teal }}> — {ref.urlLabel}</span>
                    ) : null}
                    .
                  </p>
                </cite>
              </li>
            ))}
          </ol>
        </div>
      );

    case 'faq':
      return (
        <div>
          <SectionHeading title={title} className="mb-2" />
          {section.intro ? (
            <p className="mb-3 kc-type-body" style={{ color: KC_COLORS.gray600 }}>
              {section.intro}
            </p>
          ) : null}
          <FaqAccordion faqs={section.faqs || []} idPrefix={`article-faq-${title}`} />
        </div>
      );

    case 'relatedLinks':
      return (
        <RelatedLinksSection
          title={title || 'Related Terms and Knowledge Center Content'}
          dictionaryTerms={section.dictionaryTerms || []}
          knowledgeCenter={section.knowledgeCenter || []}
        />
      );

    case 'figure':
      return <ArticleFigure figure={section} />;

    case 'charlee':
      return (
        <div
          className="rounded-2xl border p-5 md:p-7 lg:p-8"
          style={{
            borderColor: KC_COLORS.border,
            backgroundColor: KC_COLORS.tealLight,
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-8 lg:gap-10 items-center">
            <div className="text-left min-w-0">
              {title ? (
                <h2
                  className="kc-type-h3 mb-3 text-left font-bold"
                  style={{ color: KC_COLORS.teal }}
                >
                  {title}
                </h2>
              ) : null}
              <div className="max-w-xl">
                {(section.paragraphs || []).map((text) => (
                  <p
                    key={text.slice(0, 64)}
                    className="kc-type-body font-semibold"
                    style={{ color: KC_COLORS.navy700 }}
                  >
                    {text}
                  </p>
                ))}
              </div>
              {section.ctaPath ? (
                <Link
                  to={section.ctaPath}
                  className="inline-flex mt-5 items-center justify-center rounded-lg px-5 py-2.5 kc-type-body font-bold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: KC_COLORS.teal, color: '#FFFFFF' }}
                >
                  {section.ctaLabel || 'Explore Charlee.ai Platform →'}
                </Link>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5 md:gap-4 text-left">
              {(section.features || []).map((feature) => (
                <div
                  key={feature.id}
                  className="flex flex-col items-start justify-start gap-2.5 px-0.5 sm:px-1 min-w-0"
                >
                  <span
                    className="inline-flex items-center justify-center"
                    style={{ color: KC_COLORS.teal }}
                  >
                    <KcIcon
                      name={feature.icon}
                      fontSize="large"
                      sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }}
                    />
                  </span>
                  <p
                    className="kc-type-caption font-bold text-left max-w-full sm:max-w-[110px] break-words"
                    style={{ color: KC_COLORS.primaryNavy }}
                  >
                    {feature.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'bodyGroup':
      return <BodyGroupPanel section={section} hideTitle={hideTitle} />;

    case 'body':
    default:
      if (section.tone) {
        return (
          <InfoCard
            title={section.title}
            hideTitle={hideTitle}
            tone={section.tone}
            icon={section.icon || 'document'}
          >
            <Paragraphs paragraphs={section.paragraphs} />
            <Bullets bullets={section.bullets} />
          </InfoCard>
        );
      }
      return (
        <div>
          <SectionHeading title={title} className="mb-3" />
          <Paragraphs paragraphs={section.paragraphs} />
          <Bullets bullets={section.bullets} />
        </div>
      );
  }
}

function MobileSection({ section, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const label = section.title || 'Section';

  return (
    <div
      id={section.id}
      className="kc-article-section scroll-mt-28 rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden text-left"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <span
          className="kc-type-body font-bold min-w-0 break-words"
          style={{ color: KC_COLORS.primaryNavy }}
        >
          {label}
        </span>
        <ExpandMoreIcon
          sx={{
            color: '#6B7280',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </button>
      {open ? (
        <div className="px-4 pb-4 border-t border-[#E5E7EB] pt-4">
          <SectionContent section={section} hideTitle />
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} props.sections
 * @param {boolean} [props.openFirstOnMobile=false] Open only the first section on mobile; all others start collapsed.
 */
export function ArticleSections({ sections, openFirstOnMobile = false }) {
  return (
    <>
      <div className="hidden lg:block space-y-10 md:space-y-12 text-left">
        {sections.map((section) => (
          <div
            key={section.id}
            id={section.id}
            className="kc-article-section scroll-mt-28 text-left"
          >
            <SectionContent section={section} />
          </div>
        ))}
      </div>
      <div className="lg:hidden space-y-3.5 text-left">
        {sections.map((section, index) =>
          section.variant === 'figure' ? (
            <div
              key={section.id}
              id={section.id}
              className="kc-article-section scroll-mt-28 text-left"
            >
              <ArticleFigure figure={section} />
            </div>
          ) : (
            <MobileSection
              key={section.id}
              section={section}
              defaultOpen={
                section.variant === 'faq' ||
                (openFirstOnMobile && index === 0)
              }
            />
          )
        )}
      </div>
    </>
  );
}
